import { AppleIcon, BinocularsIcon, NotebookIcon, ShieldUserIcon } from 'lucide-preact';
import { ComponentProps } from 'preact';
import { ReactNode } from 'preact/compat';

import { ExperienceOption } from '@/redux/slices/user/types';

interface PersonaSelectorProps extends Omit<ComponentProps<'div'>, 'value' | 'onChange'> {
  value: ExperienceOption | null;
  onChange: (value: ExperienceOption) => void;
  /** Override the list — order and copy. Defaults to the full set used in account settings. */
  options?: PersonaOption[];
}

export interface PersonaOption {
  id: ExperienceOption;
  label: string;
  description: string;
}

/**
 * The artwork, keyed by persona. Separate from the copy so a caller can pass its own wording
 * without redefining the icons. Glyphs and disc colours are Figma `8459-7197`; `primary-yellow`
 * already is the design's `#FFCE1F`, while the greens differ, so the disc carries its own hex.
 */
export const PERSONA_ICONS: Partial<Record<ExperienceOption, ReactNode>> = {
  educator: (
    <div className="flex size-12 items-center justify-center rounded-full bg-[#F9A5E7] text-white">
      <AppleIcon className="preflight preflight-svg size-7" />
    </div>
  ),
  parent: (
    <div className="flex size-12 items-center justify-center rounded-full bg-[#F18C43] text-white">
      <ShieldUserIcon className="preflight preflight-svg size-7" />
    </div>
  ),
  researcher: (
    <div className="flex size-12 items-center justify-center rounded-full bg-primary-yellow text-white">
      <NotebookIcon className="preflight preflight-svg size-7" />
    </div>
  ),
  animalLover: (
    <div className="flex size-12 items-center justify-center rounded-full bg-[#458254] text-white">
      <BinocularsIcon className="preflight preflight-svg size-7" />
    </div>
  ),
};

/**
 * The four personas the design offers, in its order. `zooGoer` and `other` remain valid stored
 * values for people who chose them before — they are simply no longer on offer, so a returning
 * visitor holding one sees nothing pre-selected until they pick again.
 */
export const PERSONA_OPTIONS: PersonaOption[] = [
  {
    id: 'educator',
    label: 'Educator',
    description: 'Use real life animal behaviours in my lessons',
  },
  {
    id: 'parent',
    label: 'Parent',
    description: 'Explore & learn from animals with my family',
  },
  {
    id: 'researcher',
    label: 'Researcher',
    description: 'Observe and study animals',
  },
  {
    id: 'animalLover',
    label: 'Animal Lover',
    description: 'Watch animals, connect with nature, and relax.',
  },
];

const PersonaSelector = ({ value, onChange, options = PERSONA_OPTIONS, ...props }: PersonaSelectorProps) => {
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <div {...props}>
      {options.map((opt) => {
        const isSelected = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => onChange(opt.id)}
            data-component="persona-option"
            data-persona={opt.id}
            className="flex w-full cursor-pointer items-center rounded-lg border border-solid border-gray-300 bg-white p-4 transition-colors hover:bg-[#FEFFF9] aria-checked:border-primary-green aria-checked:bg-[#FEFFF9]"
          >
            <div className="mr-3 shrink-0">{PERSONA_ICONS[opt.id]}</div>
            <div className="flex flex-col text-left">
              <span className="text-base font-semibold text-[#162B1B]">{opt.label}</span>
              <span className="text-sm text-gray-500">{opt.description}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default PersonaSelector;
